import { queryAndMergeLogisticsProgress } from "../../utils/logisticsProgressLookup";

export const EQUIPMENT_GROUP_PAGE_SIZE = 50;

/**
 * @param {object} data - raw DRF page ({ count, next, previous, results })
 * @param {number} page - the page number that was requested
 * @returns {{ items: object[], count: number, page: number, hasNext: boolean, hasPrevious: boolean }}
 */
export const mapEquipmentPage = (data, page) => ({
  items: data?.results ?? [],
  count: data?.count ?? 0,
  page,
  hasNext: Boolean(data?.next),
  hasPrevious: Boolean(data?.previous),
});

export const fetchEquipmentPage = async (
  api,
  {
    plantId,
    equipmentTypeId,
    areaCode,
    search,
    page = 1,
    pageSize = EQUIPMENT_GROUP_PAGE_SIZE,
    signal,
  } = {},
) => {
  const params = { plant: plantId, page, page_size: pageSize };
  // A `null` id groups equipment with no equipmentType ("Unknown"); the
  // backend filter only understands a real id, so that bucket is left
  // unfiltered (there is only ever one, so no other equipment leaks in).
  if (equipmentTypeId != null) params.equipmentType = equipmentTypeId;
  if (areaCode) params.area_code = areaCode;
  if (search) params.search = search;

  const response = await api.get("/equipment/", { params, signal });
  return mapEquipmentPage(response.data, page);
};

// Cards draw their logistics progress tracker only for items carrying the
// fields this merge adds (raw /equipment/ rows have none); it fails soft, so
// the page still renders without progress on error. The P&ID/PFD badges'
// keyDocumentTypes already come with each row from the server. Mirrors
// fetchInstrumentsPageWithProgress.
export const fetchEquipmentPageWithEnrichment = async (api, params = {}) => {
  const page = await fetchEquipmentPage(api, params);
  const withProgress = await queryAndMergeLogisticsProgress(page.items, "equipment");
  const items = withProgress.map((item) => ({
    ...item,
    keyDocumentTypes: item.keyDocumentTypes ?? [],
  }));
  return { ...page, items };
};

// Resolves a small set of equipment ids to their full rows - used to hydrate
// already-selected values in an async equipment picker, and to resolve
// related-equipment ids for read-only display, without ever fetching the
// full plant equipment list. Fetches in parallel; a handful of ids at most.
export const fetchEquipmentByIds = async (api, ids = [], { signal } = {}) => {
  const uniqueIds = [...new Set((ids || []).filter((id) => id != null))];
  if (!uniqueIds.length) return [];

  const results = await Promise.allSettled(
    uniqueIds.map((id) => api.get(`/equipment/${id}/`, { signal })),
  );
  return results
    .filter((result) => result.status === "fulfilled")
    .map((result) => result.value.data);
};
